// 事件記錄器
// Event Logger - Central event logging and query system

import type {
  GameEvent,
  GameEventType,
  EventPayload,
  EventFilter,
  EventVisibility,
  AnimationHint,
  SerializedEventLog,
} from '../../types/events';
import type { TurnPhase } from '../../types/game';

/**
 * 事件處理器類型
 */
export type EventHandler = (event: GameEvent) => void;

/**
 * 事件記錄器
 */
export class EventLogger {
  private events: GameEvent[] = [];
  private listeners: Map<GameEventType | '*', EventHandler[]> = new Map();
  private eventCounter = 0;
  private gameId: string;
  private startTime: number;
  private currentContext: {
    turnNumber: number;
    phase: TurnPhase;
    activePlayerId: string;
  };

  constructor(gameId?: string) {
    this.gameId = gameId ?? this.generateGameId();
    this.startTime = Date.now();
    this.currentContext = {
      turnNumber: 1,
      phase: 'draw',
      activePlayerId: '',
    };
  }

  /**
   * 生成遊戲 ID
   */
  private generateGameId(): string {
    return 'game-' + Date.now().toString(36) + '-' + Math.random().toString(36).substring(2, 9);
  }

  /**
   * 生成事件 ID
   */
  private generateEventId(): string {
    this.eventCounter++;
    return 'evt-' + this.eventCounter.toString().padStart(6, '0');
  }

  /**
   * 更新上下文
   */
  updateContext(context: Partial<typeof this.currentContext>): void {
    this.currentContext = { ...this.currentContext, ...context };
  }

  /**
   * 記錄事件
   */
  log(
    type: GameEventType,
    payload: EventPayload,
    options?: {
      playerId?: string;
      triggeredBy?: string;
      visibility?: EventVisibility;
      animation?: AnimationHint;
      tags?: string[];
    }
  ): GameEvent {
    const event: GameEvent = {
      id: this.generateEventId(),
      timestamp: Date.now(),
      turnNumber: this.currentContext.turnNumber,
      phase: this.currentContext.phase,
      playerId: options?.playerId ?? this.currentContext.activePlayerId,
      type,
      payload,
      triggeredBy: options?.triggeredBy,
      visibility: options?.visibility ?? 'public',
      animation: options?.animation,
      tags: options?.tags,
    };

    // 如果有父事件，更新父事件的 triggeredEvents
    if (event.triggeredBy) {
      const parentEvent = this.events.find(e => e.id === event.triggeredBy);
      if (parentEvent) {
        parentEvent.triggeredEvents = parentEvent.triggeredEvents ?? [];
        parentEvent.triggeredEvents.push(event.id);
      }
    }

    this.events.push(event);
    this.emit(event);
    
    return event;
  }

  /**
   * 發送事件給監聽器
   */
  private emit(event: GameEvent): void {
    // 發送給特定類型的監聽器
    const typeListeners = this.listeners.get(event.type) ?? [];
    typeListeners.forEach(handler => {
      handler(event);
    });

    // 發送給萬用監聽器
    const allListeners = this.listeners.get('*') ?? [];
    allListeners.forEach(handler => {
      handler(event);
    });
  }

  /**
   * 訂閱事件
   */
  on(type: GameEventType | '*', handler: EventHandler): () => void {
    const handlers = this.listeners.get(type) ?? [];
    handlers.push(handler);
    this.listeners.set(type, handlers);

    // 返回取消訂閱函數
    return () => {
      const idx = handlers.indexOf(handler);
      if (idx !== -1) {
        handlers.splice(idx, 1);
      }
    };
  }

  /**
   * 取消所有訂閱
   */
  offAll(): void {
    this.listeners.clear();
  }

  /**
   * 取得所有事件
   */
  getEvents(filter?: EventFilter): GameEvent[] {
    if (!filter) {
      return [...this.events];
    }

    return this.events.filter(event => {
      if (filter.types && !filter.types.includes(event.type)) return false;
      if (filter.playerId && event.playerId !== filter.playerId) return false;
      if (filter.turnNumber !== undefined && event.turnNumber !== filter.turnNumber) return false;
      if (filter.phase && event.phase !== filter.phase) return false;
      if (filter.visibility && event.visibility !== filter.visibility) return false;
      if (filter.fromTimestamp && event.timestamp < filter.fromTimestamp) return false;
      if (filter.toTimestamp && event.timestamp > filter.toTimestamp) return false;
      if (filter.tags && filter.tags.length > 0) {
        if (!event.tags || !filter.tags.some(t => event.tags?.includes(t))) return false;
      }
      if (filter.cardId) {
        const payload = event.payload as Record<string, unknown>;
        const hasCardId = 
          payload.cardId === filter.cardId ||
          payload.sourceCardId === filter.cardId ||
          payload.targetCardId === filter.cardId ||
          payload.attackerId === filter.cardId ||
          payload.defenderId === filter.cardId;
        if (!hasCardId) return false;
      }
      return true;
    });
  }

  /**
   * 取得最近 N 個事件
   */
  getRecentEvents(count: number): GameEvent[] {
    return this.events.slice(-count);
  }

  /**
   * 取得某事件的因果鏈 (向上追溯)
   */
  getCausalityChain(eventId: string): GameEvent[] {
    const chain: GameEvent[] = [];
    let currentEvent = this.events.find(e => e.id === eventId);
    
    while (currentEvent) {
      chain.unshift(currentEvent);
      if (currentEvent.triggeredBy) {
        currentEvent = this.events.find(e => e.id === currentEvent?.triggeredBy);
      } else {
        break;
      }
    }
    
    return chain;
  }

  /**
   * 取得某事件觸發的所有後續事件 (向下展開)
   */
  getTriggeredEvents(eventId: string): GameEvent[] {
    const result: GameEvent[] = [];
    const queue: string[] = [eventId];
    
    while (queue.length > 0) {
      const currentId = queue.shift();
      const currentEvent = this.events.find(e => e.id === currentId);
      
      if (currentEvent && currentId !== eventId) {
        result.push(currentEvent);
      }
      
      if (currentEvent?.triggeredEvents) {
        queue.push(...currentEvent.triggeredEvents);
      }
    }
    
    return result;
  }

  /**
   * 取得某回合的所有事件
   */
  getEventsForTurn(turnNumber: number): GameEvent[] {
    return this.events.filter(e => e.turnNumber === turnNumber);
  }

  /**
   * 取得某玩家可見的事件
   */
  getVisibleEvents(playerId: 'player1' | 'player2'): GameEvent[] {
    return this.events.filter(e => 
      e.visibility === 'public' || e.visibility === playerId
    );
  }

  /**
   * 匯出事件紀錄
   */
  export(players: SerializedEventLog['players']): SerializedEventLog {
    return {
      version: '1.0.0',
      gameId: this.gameId,
      startTime: this.startTime,
      endTime: Date.now(),
      players,
      events: [...this.events],
    };
  }

  /**
   * 從紀錄匯入
   */
  static fromExport(log: SerializedEventLog): EventLogger {
    const logger = new EventLogger(log.gameId);
    logger.startTime = log.startTime;
    logger.events = [...log.events];
    logger.eventCounter = log.events.length;
    return logger;
  }

  /**
   * 清除所有事件
   */
  clear(): void {
    this.events = [];
    this.eventCounter = 0;
  }

  /**
   * 取得事件數量
   */
  get count(): number {
    return this.events.length;
  }

  /**
   * 取得遊戲 ID
   */
  get id(): string {
    return this.gameId;
  }
}

/**
 * 便利方法：建立帶有動畫提示的事件
 */
export function createAnimatedEvent(
  animation: Partial<AnimationHint>
): AnimationHint {
  return {
    type: animation.type ?? 'effect',
    duration: animation.duration ?? 500,
    priority: animation.priority ?? 0,
    data: animation.data,
  };
}

/**
 * 便利方法：建立卡牌移動動畫
 */
export function createMoveAnimation(
  from: { x: number; y: number },
  to: { x: number; y: number },
  duration = 300
): AnimationHint {
  return {
    type: 'move',
    duration,
    priority: 1,
    data: { fromPosition: from, toPosition: to },
  };
}

/**
 * 便利方法：建立效果動畫
 */
export function createEffectAnimation(
  effectName: string,
  duration = 500
): AnimationHint {
  return {
    type: 'effect',
    duration,
    priority: 2,
    data: { effectName },
  };
}

/**
 * 便利方法：建立文字動畫
 */
export function createTextAnimation(
  text: string,
  color = '#ffffff',
  duration = 1000
): AnimationHint {
  return {
    type: 'text',
    duration,
    priority: 3,
    data: { text, color },
  };
}
